import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '../../Modals/Modal'
import { GridColumn } from '../Grid'
import './GridOptions.scss'

interface GridOptionsProps<T> {
  title?: string
  icon?: string
  columns: GridColumn<T>[]
  rowsPerPage: number
  filtersVisible: boolean
  defaultColumns: GridColumn<T>[]
  onColumnsChange: (columns: GridColumn<T>[]) => void
  onRowsPerPageChange: (rowsPerPage: number) => void
  onFiltersVisibleChange: (filtersVisible: boolean) => void
  onExport?: () => void
  onReload?: () => void
}

export function GridOptions<T>(props: GridOptionsProps<T>) {
  const [columnOptionsVisible, setColumnOptionsVisible] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number>()
  const [dragOverIndex, setDragoverIdx] = useState<number>()
  const { t } = useTranslation()

  const clearFilters = () => {
    const updatedColumns = props.columns.map((c) => ({
      ...c,
      filter: undefined,
    }))
    props.onColumnsChange(updatedColumns)
  }

  const resetColumns = () => {
    const updatedColumns: GridColumn<T>[] = []
    for (let i = 0; i < props.columns.length; i++) {
      const column: GridColumn<T> = { ...props.columns[i] }
      const idx = props.defaultColumns.findIndex((c) => c.key === column.key)
      if (idx > -1) {
        const defaultColumn = props.defaultColumns[idx]
        column.hide = defaultColumn.hide
      }
      updatedColumns.splice(idx > -1 ? idx : i, 0, column)
    }
    setActiveIndex(undefined)
    setDragoverIdx(undefined)
    props.onColumnsChange(updatedColumns)
  }

  const dragStartHandler = useCallback((ev: React.DragEvent<HTMLDivElement>, index: number) => {
    ev.dataTransfer.setData('columnIndex', `${index}`)
    ev.dataTransfer.effectAllowed = 'move'
    setActiveIndex(index)
  }, [])

  const dragOverHandler = useCallback(
    (ev: React.DragEvent<HTMLDivElement>, index: number) => {
      ev.preventDefault()
      if (activeIndex !== index) {
        setDragoverIdx(index)
      }
      if (activeIndex === index) {
        setDragoverIdx(undefined)
      }
      ev.dataTransfer.dropEffect = 'move'
    },
    [activeIndex]
  )

  const dropHandler = (ev: React.DragEvent<HTMLDivElement>, index: number) => {
    ev.preventDefault()
    const draggedColumnIndex = ev.dataTransfer.getData('columnIndex')
    if (Number(draggedColumnIndex) === index) return
    const updatedColumns = [...props.columns]
    const [draggedColumn] = updatedColumns.splice(Number(draggedColumnIndex), 1)
    updatedColumns.splice(index, 0, draggedColumn)
    props.onColumnsChange(updatedColumns)
    setActiveIndex(index)
    setDragoverIdx(undefined)
  }

  const handleManualReorder = (
    ev: React.MouseEvent<HTMLElement, MouseEvent>,
    index: number,
    direction: 'up' | 'down'
  ) => {
    ev.preventDefault()
    const updatedColumns = [...props.columns]

    if (direction === 'up' && index > 0) {
      const toIndex = updatedColumns.findLastIndex((c, i) => !c.parentKey && i < index)
      if (toIndex > -1) {
        const [selectedColumn] = updatedColumns.splice(index, 1)
        updatedColumns.splice(toIndex, 0, selectedColumn)
        setActiveIndex(toIndex)
      }
    } else if (direction === 'down') {
      const toIndex = updatedColumns.findIndex((c, i) => !c.parentKey && i > index)
      if (toIndex > -1) {
        const [selectedColumn] = updatedColumns.splice(index, 1)
        updatedColumns.splice(toIndex, 0, selectedColumn)
        setActiveIndex(toIndex)
      }
    }

    props.onColumnsChange(updatedColumns)
  }

  const changeColVisibility = (e: React.ChangeEvent<HTMLInputElement>) => {
    const key = e.target.name
    const updatedColumns = props.columns.map((c) => {
      if (c.key === key) {
        return { ...c, hide: !c.hide }
      }
      return c
    })
    props.onColumnsChange(updatedColumns)
  }

  return (
    <>
      <div className="grid-filter-section">
        <div className="body">
          <div className="left">
            <div className="head">
              {!!props.icon && <span className={props.icon}></span>}
              {!!props.title && <span>{props.title}</span>}
            </div>
          </div>
          <div className="right">
            {props.columns.some((c) => c.filter && c.filter.length) && (
              <a onClick={clearFilters}>
                <span className="icon-clear"></span> Clear filters
              </a>
            )}

            <a onClick={() => props.onFiltersVisibleChange(!props.filtersVisible)}>
              <span className={props.filtersVisible ? 'icon-hide' : 'icon-show'}></span> {t('TOGGLE_FILTERS')}
            </a>
            <a onClick={() => setColumnOptionsVisible(true)}>
              <span className="icon-settings"></span> {t('CUSTOMIZE_GRID_COLUMNS')}
            </a>
            {props.onExport && (
              <a onClick={() => props.onExport && props.onExport()}>
                <span className="icon-excel"></span> {t('EXPORT_CONTRACT_TO_EXCEL')}
              </a>
            )}
            {props.onReload && (
              <a onClick={() => props.onReload && props.onReload()}>
                <span className="icon-reload"></span> {t('RELOAD')}
              </a>
            )}
            <div className="perPage">
              <div className="selectArea">
                <select
                  value={props.rowsPerPage.toString()}
                  onChange={(e) => props.onRowsPerPageChange(Number(e.target.value))}
                >
                  {['0', '10', '25', '50', '100'].map((value, index) => (
                    <option value={value} key={index}>
                      {value === '0' ? 'All' : value}
                    </option>
                  ))}
                </select>
                <i className="icon-bottom" />
              </div>
              {t('ITEMS_PER_PAGE')}
            </div>
          </div>
        </div>
      </div>
      <Modal
        visible={columnOptionsVisible}
        onClose={() => setColumnOptionsVisible(false)}
        title="Customize columns"
        resetBtn={true}
        resetOrder={resetColumns}
      >
        <div className="grid-cols-cc reorderColumn">
          {props.columns.map(
            (cs, idx) =>
              !cs.parentKey &&
              !!cs.name && (
                <div
                  key={cs.key}
                  draggable="true"
                  onDragStart={(ev) => dragStartHandler(ev, idx)}
                  onDragOver={(ev) => dragOverHandler(ev, idx)}
                  onDrop={(ev) => dropHandler(ev, idx)}
                  className={`col-wrap flex between enabled  ${activeIndex === idx ? `active` : ''}  ${
                    dragOverIndex === idx ? (idx > activeIndex! ? 'fromBelow' : `fromAbove`) : ''
                  }`}
                >
                  <label>
                    <input type="checkbox" name={cs.key} checked={!cs?.hide} onChange={(e) => changeColVisibility(e)} />
                    <span>{cs.label ?? cs.name}</span>
                  </label>
                  <div className="arrowArea">
                    {idx !== 0 && <i className="icon-up" onClick={(ev) => handleManualReorder(ev, idx, 'up')} />}
                    {idx !== props.columns.length - 1 && (
                      <i className="icon-bottom" onClick={(ev) => handleManualReorder(ev, idx, 'down')} />
                    )}
                    <i className="icon-drag" />
                  </div>
                </div>
              )
          )}
        </div>
      </Modal>
    </>
  )
}
